import React, { useEffect, useRef, useState } from "react";
import { ZoomIn, ZoomOut } from "lucide-react";
import classes from "./Resume.module.css";

const file = require("../../assets/HuaCatLuong-Senior_Mobile_Platform_Engineer.pdf");
const workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const Resume = () => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const renderIdRef = useRef(0);
  const [pageCount, setPageCount] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [viewportVersion, setViewportVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const renderId = renderIdRef.current + 1;
    renderIdRef.current = renderId;

    const renderPdf = async () => {
      const container = containerRef.current;

      if (!container) {
        return;
      }

      setLoading(true);
      setError("");
      container.replaceChildren();

      try {
        const pdfjs = await import("pdfjs-dist");
        pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;

        const pdf = await pdfjs.getDocument(file).promise;

        if (cancelled || renderId !== renderIdRef.current) {
          return;
        }

        setPageCount(pdf.numPages);

        const availableWidth = Math.max(
          320,
          container.clientWidth - (window.innerWidth < 768 ? 24 : 64),
        );
        const devicePixelRatio = Math.min(window.devicePixelRatio || 1, 2);

        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
          if (cancelled || renderId !== renderIdRef.current) {
            return;
          }

          const page = await pdf.getPage(pageNumber);
          const baseViewport = page.getViewport({ scale: 1 });
          const displayScale = (availableWidth / baseViewport.width) * zoom;
          const displayViewport = page.getViewport({ scale: displayScale });
          const renderViewport = page.getViewport({
            scale: displayScale * devicePixelRatio,
          });

          const pageWrapper = document.createElement("section");
          pageWrapper.className = classes.page;
          pageWrapper.setAttribute("aria-label", `Resume page ${pageNumber}`);

          const canvas = document.createElement("canvas");
          canvas.className = classes.canvas;
          canvas.width = Math.floor(renderViewport.width);
          canvas.height = Math.floor(renderViewport.height);
          canvas.style.width = `${Math.floor(displayViewport.width)}px`;
          canvas.style.height = `${Math.floor(displayViewport.height)}px`;

          pageWrapper.appendChild(canvas);
          container.appendChild(pageWrapper);

          const canvasContext = canvas.getContext("2d");

          if (!canvasContext) {
            throw new Error("Could not render resume page.");
          }

          await page.render({
            canvasContext,
            viewport: renderViewport,
          }).promise;
        }
      } catch (_error) {
        if (!cancelled) {
          setError("Could not display the resume.");
        }
      } finally {
        if (!cancelled && renderId === renderIdRef.current) {
          setLoading(false);
        }
      }
    };

    renderPdf();

    return () => {
      cancelled = true;
    };
  }, [zoom, viewportVersion]);

  useEffect(() => {
    let resizeTimeout: number | undefined;

    const handleResize = () => {
      window.clearTimeout(resizeTimeout);
      resizeTimeout = window.setTimeout(() => {
        setViewportVersion((currentVersion) => currentVersion + 1);
      }, 150);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.clearTimeout(resizeTimeout);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const zoomOut = () => {
    setZoom((currentZoom) => Math.max(0.6, Number((currentZoom - 0.1).toFixed(1))));
  };

  const zoomIn = () => {
    setZoom((currentZoom) => Math.min(1.8, Number((currentZoom + 0.1).toFixed(1))));
  };

  return (
    <main className={classes.resume}>
      <div className={classes.toolbar} aria-label="Resume viewer controls">
        <span className={classes.status}>
          {pageCount > 0 ? `${pageCount} page${pageCount > 1 ? "s" : ""}` : "Resume"}
        </span>
        <div className={classes.controls}>
          <button
            type="button"
            className={classes.iconButton}
            onClick={zoomOut}
            disabled={zoom <= 0.6}
            aria-label="Zoom out"
            title="Zoom out"
          >
            <ZoomOut size={18} strokeWidth={1.8} />
          </button>
          <span className={classes.zoomLabel}>{Math.round(zoom * 100)}%</span>
          <button
            type="button"
            className={classes.iconButton}
            onClick={zoomIn}
            disabled={zoom >= 1.8}
            aria-label="Zoom in"
            title="Zoom in"
          >
            <ZoomIn size={18} strokeWidth={1.8} />
          </button>
        </div>
      </div>

      {loading && <div className={classes.message}>Loading resume...</div>}
      {error && <div className={classes.message}>{error}</div>}
      <div ref={containerRef} className={classes.pages} />
    </main>
  );
};

export default Resume;
